import React from 'react';
import { Route, withRouter } from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';
import { Loader, WidgetPrintHeader } from '@mummssoftware/common-ui';
import { isUserDoctorRoleGroup } from '@mummssoftware/utils/utils';
import { withMummsPDFGlobalContext } from '@mummssoftware/mumms-print';
import { mobileDetect, isSmallTouchDevice } from '@mummssoftware/utils/web';
import styled from 'styled-components';
import { ReactPDFString } from '@mummssoftware/utils/constants';
import { CertFaxProvider } from '../Fax/CertFaxProvider';
import { CertEmailProvider } from '../Email/CertEmailProvider';
import PatientCert from '../Patient/PatientCert';
import Listing from '../Listing/Listing';
import { AppContext } from './AppContext';
import { withCertContext } from '../../Context';
import { ApprovalContextProvider } from '../Listing/Context/ApprovalContext';
import { RESTRICTED_ROLES } from '../../constants';
import { hideMedColumn as HideMedColumn } from '../../utils';
import messages from '../CertForm/CertMessages';
import { ReviewContextProvider } from '../Listing/Context/ReviewContext';
const appRootHeights = {
  height: (props) => (props.embedded ? 'initial' : '100vh'),
  '@media print': {
    '&&': {
      height: 'auto',
    },
  },
};
// var are defined in defaultTheme
const AppRoot = withStyles((theme) => ({
  appRoot: {
    ...(theme.props.isSafari && theme.props.isMobile ? {} : appRootHeights),
    marginLeft: 'var(--safe-area-inset-left)',
    marginRight: 'var(--safe-area-inset-right)',
    marginBottom: 'var(--safe-area-inset-bottom)',
    overflow: 'hidden',
  },
}))(({ children, classes: { appRoot } }) => (
  <div className={appRoot}>{children}</div>
));
// TODO min-height
const CertAppBodyWrap = styled.div`
  max-width: ${(props) => (props.embedded ? '1400' : '1100')};
  min-height: ${(props) => props.embedded && 279}px;
  min-width: ${(props) => props.embedded && 250}px;
  margin: 2px auto;
  height: calc(100% - 4px);
`;
// with print needs a width
const CertAppPrintDiv = styled.div`
  width: auto;
  margin: auto;
  @media print {
    page-break-after: always;
    width: auto;
    max-width: 750px;
  }
`;
const PageBreak = styled.div`
  @media print {
    visibility: hidden;
  }
`;
const singlePatientViews = (pathname) =>
  !['recert', 'history'].find((path) => pathname.includes(path));

class App extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      firstName: '',
      lastName: '',
      siteId: 17,
      siteList: [],
      isDoctor: null,
      loaded: false,
      queryParams: props.location.search,
      currentUser: null,
    };
  }

  updateUserInfo = async () => {
    const { pim, setDoctors, clearScripts } = this.props;
    const data = await pim.getUserInfo(this.props.hospiceBin, RESTRICTED_ROLES);
    if (data && this.mounted) {
      const searchParams = new URLSearchParams(window.location.search);
      // site Id from URL if present
      const siteId =
        Number(searchParams.get('siteId')) > 0
          ? Number(searchParams.get('siteId'))
          : data.siteId;
      // NOTE this is because we have a problem with siteId not coming from URL-
      const siteName = siteId
        ? this.getSiteInfo(siteId, data.siteList, 'name')
        : data.siteName;

      const siteNickname = siteId
        ? this.getSiteInfo(siteId, data.siteList, 'nickName')
        : data.siteNickname;

      // Mumms / cross-hospice users often have no currentHospiceBin; PIM and
      // workflow still need a tenant id — fall back to URL / CertContext agency.
      const hospiceAgency =
        (data.currentHospiceBin && String(data.currentHospiceBin).trim()) ||
        (this.props.agency && String(this.props.agency).trim()) ||
        (this.props.hospiceBin && String(this.props.hospiceBin).trim()) ||
        null;

      setDoctors(siteId, hospiceAgency);
      const hbcLocation = this.getSiteInfo(
        data.siteId,
        data.siteList,
        'hbcLocation',
      );
      this.setState({
        currentUser: data,
        loaded: true,
        firstName: data.firstName,
        lastName: data.lastName,
        userId: data.userId,
        userNumber: data.personNumber,
        // passing agency because we need it for clearscripts
        agency: hospiceAgency,
        userCredentials: data.userCredentials,
        // site Id from URL if present
        siteId,
        hospiceBin: hospiceAgency,
        hospiceName: data.hospiceName,
        siteName,
        siteNickname,
        siteList: data.siteList,
        isDoctor: data.isDoctor,
        isNP: data.isNP,
        isMumms: data.isMumms,
        enableClinicalLinks: !!(data.hbcFirm && hbcLocation),
        hbcFirm: data.hbcFirm,
        hbcLocation,
        isDoctorRoleGroup: isUserDoctorRoleGroup(data),
        hideMedColumn: HideMedColumn(hospiceAgency),
      });

      if (data.clearScriptsLogin && siteNickname) {
        const rxData = await clearScripts.getNotifications(siteNickname);
        if (!(rxData instanceof Response)) {
          /* eslint-disable indent */

          const rxDataCount = rxData.length
            ? rxData.reduce((seed, val) => {
                if (seed[val.action]) {
                  seed[val.action] += val.count;
                } else {
                  seed[val.action] = val.count;
                }
                return seed;
              }, {})
            : null;
          if (rxDataCount) {
            const { rxmessage, rxsign } = rxDataCount;
            this.setState({
              rxmessage,
              rxsign,
            });
          }
        }
      }
    }
  };

  getAgency = () => {
    const { agency } = this.state;
    return (
      (agency && String(agency).trim()) ||
      this.props.agency ||
      this.props.hospiceBin ||
      null
    );
  };

  smartChartUrl = (patientNumber) => {
    const { agency, siteId } = this.state;
    const scHost =
      window.env?.hosts?.smartchartUrl || process.env.REACT_APP_SMARTCHART_URL;
    return `${scHost}/${patientNumber}?agency=${agency}&siteId=${siteId}&embedded=true`;
  };

  getSiteInfo = (siteId, siteList, key) => {
    const currentSite = siteList.length
      ? siteList.find(({ id }) => id === siteId)
      : null;
    return currentSite ? currentSite[key] : null;
  };

  handleAgencyChange = (value) => {
    if (typeof value === 'string' && value !== this.state.hospiceBin) {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set('agency', value);
      const newRelativePathQuery = `${
        window.location.pathname
      }?${searchParams.toString()}`;
      this.props.history.push(newRelativePathQuery);
      /* eslint-disable no-restricted-globals */
      location.reload();
      /* eslint-enable */
    }
  };

  handleSiteChange = (siteId) => {
    const { setDoctors } = this.props;
    setDoctors(siteId, this.state.agency ?? this.props.agency);

    localStorage.setItem('siteId-user', siteId);
    this.setState((prevState) => {
      const hbcLocation = this.getSiteInfo(
        siteId,
        prevState.siteList,
        'hbcLocation',
      );
      const siteNickname = this.getSiteInfo(
        siteId,
        prevState.siteList,
        'nickName',
      );
      return {
        siteId,
        siteNickname,
        hbcLocation,
        enableClinicalLinks: !!(prevState.hbcFirm && hbcLocation),
      };
    });
  };

  componentDidMount = () => {
    this.startLoadingTime = Date.now();

    console.log('** DOC LINK WIDGET START **', new Date());
    this.mounted = true;
    this.updateUserInfo();
  };

  componentWillUnmount = () => {
    this.mounted = false;
  };

  render() {
    const {
      enableClinicalLinks,
      firstName,
      lastName,
      hbcLocation,
      hospiceBin,
      hospiceName,
      isMumms,
      isDoctor,
      isDoctorRoleGroup,
      isNP,
      loaded,
      siteId,
      siteList,
      siteName,
      siteNickname,
      queryParams,
      userId,
      userNumber,
      rxmessage,
      rxsign,
      agency,
      userCredentials,
      hideMedColumn,
      currentUser,
    } = this.state;
    const {
      dateFrom,
      dateTo,
      embedded,
      location: { pathname },
      insuranceCarrierId,
      onError,
      logout,
      keycloak,
      clearScripts,
      workflow,
      viewMode,
      patientNumber,
      printConfig,
      observation,
      pim,
      resetVerbal,
    } = this.props;
    const isMobile = mobileDetect();
    const smallTouchDevice = isSmallTouchDevice();
    const appContext = {
      userCredentials: this.state.userCredentials,
      isDoctor: this.state.isDoctor,
      isDoctorRoleGroup: this.state.isDoctorRoleGroup,
      isNP: this.state.isNP,
      enableClinicalLinks: this.state.enableClinicalLinks,
      smartChartUrl: this.smartChartUrl,
      dateFrom,
      dateTo,
      firstName,
      lastName,
      hospiceBin,
      hospiceName,
      isMobile,
      isMumms,
      logout,
      siteName,
      userId,
      agency,
      userNumber,
      keycloak: this.props.keycloak,
      hideMedColumn,
      siteId,
      getAgency: this.getAgency,
      currentUser,
    };
    /**
     * The PatientCert has its own Loader, defer to that instance to avoid awkward re-render.
     *
     * TODO: Refactor Loader to top-level triggerable modal?
     */
    const isSinglePatientView = singlePatientViews(pathname);

    if (!loaded && isSinglePatientView) {
      return <Loader />;
    }

    // isPrintMode is a read only view from dashboard, this is history route in stand alone mode
    const isPrintMode = viewMode === 'print';
    // isEmbeddedMode is the widget in dashboard, this is recert route in stand alone mode
    const isEmbeddedMode = viewMode === 'small' || viewMode === 'full';
    // isStandAloneMode is docLink stand-alone mode
    const isStandAloneMode = !viewMode;

    const isReactPDFMode = viewMode === ReactPDFString;

    return (
      <AppContext.Provider value={appContext}>
        <CertEmailProvider>
          <CertFaxProvider>
            <ApprovalContextProvider>
              <ReviewContextProvider>
                {isReactPDFMode && (
                  <div id="cert-print">
                    <CertAppPrintDiv>
                      <PatientCert
                        historicalView
                        getAgency={this.getAgency}
                        isSinglePatientView
                        logout={logout}
                        loaded={loaded}
                        Context={AppContext}
                        isMobile={isMobile}
                        smallTouchDevice={smallTouchDevice}
                        patientNumber={patientNumber}
                        insuranceCarrierId={insuranceCarrierId}
                        onWidgetReady={this.props.onWidgetReady}
                        printConfig={printConfig}
                      />
                    </CertAppPrintDiv>
                    <PageBreak />
                  </div>
                )}
                {isPrintMode && (
                  <div id="cert-print">
                    <WidgetPrintHeader
                      title={messages.printTitle.defaultMessage}
                    />
                    <CertAppPrintDiv>
                      <PatientCert
                        historicalView
                        getAgency={this.getAgency}
                        isSinglePatientView
                        logout={logout}
                        loaded={loaded}
                        Context={AppContext}
                        isMobile={isMobile}
                        smallTouchDevice={smallTouchDevice}
                        patientNumber={patientNumber}
                        insuranceCarrierId={insuranceCarrierId}
                        onWidgetReady={this.props.onWidgetReady}
                        printConfig={printConfig}
                      />
                    </CertAppPrintDiv>
                    <PageBreak />
                  </div>
                )}
                {isEmbeddedMode && (
                  <AppRoot embedded>
                    <CertAppBodyWrap embedded id="cert-embedded">
                      <PatientCert
                        keycloak={keycloak}
                        isSinglePatientView
                        getAgency={this.getAgency}
                        logout={logout}
                        clearScripts={clearScripts}
                        workflow={workflow}
                        observation={observation}
                        pim={pim}
                        {...{
                          firstName,
                          lastName,
                          siteId,
                          siteName,
                          siteNickname,
                          siteList,
                          enableClinicalLinks,
                          hospiceBin,
                          hospiceName,
                          hbcLocation,
                          isMobile,
                          isMumms,
                          userId,
                          userNumber,
                          isDoctor,
                          isDoctorRoleGroup,
                          isNP,
                          rxmessage,
                          rxsign,
                          agency,
                          userCredentials,
                          hideMedColumn,
                          currentUser,
                        }}
                        embedded={embedded}
                        loaded={loaded}
                        Context={AppContext}
                        isMobile={isMobile}
                        updateUserInfo={this.updateUserInfo}
                        patientNumber={patientNumber}
                        certIndex={new URLSearchParams(queryParams).get('cert')}
                        electionNumber={new URLSearchParams(queryParams).get(
                          'electionNumber',
                        )}
                        onCancel={onError}
                        insuranceCarrierId={insuranceCarrierId}
                        onWidgetReady={this.props.onWidgetReady}
                        startLoadingTime={this.startLoadingTime}
                      />
                    </CertAppBodyWrap>
                  </AppRoot>
                )}
                {isStandAloneMode && (
                  <AppRoot>
                    <Route
                      path="/"
                      exact
                      render={(props) => (
                        <Listing
                          {...props}
                          resetVerbal={resetVerbal}
                          PatientCert={PatientCert}
                          getAgency={this.getAgency}
                          updateUserInfo={this.updateUserInfo}
                          handleAgencyChange={this.handleAgencyChange}
                          handleSiteChange={this.handleSiteChange}
                          smartChartUrl={this.smartChartUrl}
                          certContext={AppContext}
                          keycloak={keycloak}
                          logout={logout}
                          clearScripts={clearScripts}
                          workflow={workflow}
                          observation={observation}
                          pim={pim}
                          {...{
                            firstName,
                            lastName,
                            siteId,
                            siteName,
                            siteNickname,
                            siteList,
                            enableClinicalLinks,
                            hospiceBin,
                            hospiceName,
                            hbcLocation,
                            isMobile,
                            isMumms,
                            userId,
                            userNumber,
                            isDoctor,
                            isDoctorRoleGroup,
                            isNP,
                            rxmessage,
                            rxsign,
                            agency,
                            userCredentials,
                            hideMedColumn,
                            currentUser,
                          }}
                        />
                      )}
                    />
                    <Route
                      path="/recert/:patientNumber"
                      exact
                      render={(props) => (
                        <CertAppBodyWrap id="cert-wrap">
                          <PatientCert
                            {...props}
                            keycloak={keycloak}
                            getAgency={this.getAgency}
                            isSinglePatientView
                            logout={logout}
                            embedded={embedded}
                            loaded={loaded}
                            Context={AppContext}
                            isMobile={isMobile}
                            updateUserInfo={this.updateUserInfo}
                            patientNumber={props.match.params.patientNumber}
                            certIndex={new URLSearchParams(queryParams).get(
                              'cert',
                            )}
                            electionNumber={new URLSearchParams(
                              queryParams,
                            ).get('electionNumber')}
                            onCancel={onError}
                            insuranceCarrierId={insuranceCarrierId}
                          />
                        </CertAppBodyWrap>
                      )}
                    />
                    {/* as of now the history route is not being used in stand alone mode */}
                    <Route
                      path="/history/:patientNumber"
                      exact
                      render={(props) => (
                        <div id="cert-wrap">
                          <PatientCert
                            {...props}
                            historicalView
                            getAgency={this.getAgency}
                            isSinglePatientView
                            logout={logout}
                            embedded={embedded}
                            loaded={loaded}
                            Context={AppContext}
                            isMobile={isMobile}
                            updateUserInfo={this.updateUserInfo}
                            patientNumber={props.match.params.patientNumber}
                            certIndex={new URLSearchParams(queryParams).get(
                              'cert',
                            )}
                            electionNumber={new URLSearchParams(
                              queryParams,
                            ).get('electionNumber')}
                            onCancel={onError}
                            insuranceCarrierId={insuranceCarrierId}
                            onWidgetReady={this.props.onWidgetReady}
                          />
                        </div>
                      )}
                    />
                  </AppRoot>
                )}
              </ReviewContextProvider>
            </ApprovalContextProvider>
          </CertFaxProvider>
        </CertEmailProvider>
      </AppContext.Provider>
    );
  }
}

export default withRouter(withMummsPDFGlobalContext(withCertContext(App)));
